<template>
  <div id="espacio_campos">
    <div v-if="evento.familia?.has_family" class="family-notice">
      <b>{{ evento.familia?.is_child ? 'Subevento vinculado' : 'Evento principal' }}</b>
      <span v-if="evento.familia?.is_child">
        Valida duplicados con {{ evento.familia?.parent?.nombre || 'su evento principal' }}. Los campos heredados no se pueden editar desde aquí.
      </span>
      <span v-else>
        Este evento comparte la validación de duplicados con sus subeventos. Los cambios a campos comunes se propagan a ellos.
      </span>
    </div>

    <div class="switch-container mb-4 flex items-center" :class="{ disabled: evento.familia?.has_family }">
      <span class="mr-2 text-sm font-medium text-gray-700 cursor-pointer" @click="$emit('toggle-duplicados')">
        Permitir usuarios duplicados
      </span>
      <div class="relative cursor-pointer" @click="$emit('toggle-duplicados')">
        <div :class="evento.permitir_duplicados ? 'bg-green-500' : 'bg-gray-600'" class="switch-bg"></div>
        <div :class="evento.permitir_duplicados ? 'translate-x-9' : 'translate-x-1'" class="switch-dot"></div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="camposVisible" id="tabla_campos" class="grid grid-cols-1 gap-3">
        <div
          v-for="(campo, idx) in campos"
          :key="campo.id_campo ?? campo.name_campo ?? idx"
          class="campo-registro flex justify-between items-center"
          :class="{ 'is-dragging': draggingIndex === idx, 'is-drag-over': dragOverIndex === idx }"
          :draggable="!campo.heredado"
          @dragstart="onDragStart(idx, $event)"
          @dragenter.prevent="onDragEnter(idx)"
          @dragover.prevent
          @drop.prevent="onDrop(idx)"
          @dragend="onDragEnd"
        >
          <div class="drag-handle" title="Arrastra para cambiar el orden" aria-label="Arrastra para cambiar el orden">
            <span></span><span></span><span></span>
            <span></span><span></span><span></span>
          </div>

          <div class="flex-grow campo-left">
            <input
              type="checkbox"
              :id="campo.name_campo"
              :checked="!!campo.activo"
              :disabled="campo.heredado"
              class="hidden"
              @change="onActivoChange(campo, $event)"
            />

            <label :for="campo.name_campo" :class="{ activo: campo.activo, 'cursor-pointer': true }">
              <span class="label-text">
                {{ campo.nombre_del_campo || campo.name_campo }}

                <small v-if="campo.is_custom && campo.nombre_del_campo_en" class="translation-preview">
                  EN: {{ campo.nombre_del_campo_en }}
                </small>

                <span v-if="campo.is_custom" class="tag-custom">Custom</span>
                <span v-if="campo.heredado" class="tag-inherited">Heredado</span>

                <!-- Tag tipo UI -->
                <span v-if="campo.tipo_ui === 'select'" class="tag-ui">Select</span>
                <span v-else-if="campo.tipo_ui === 'multicheck'" class="tag-ui">Multi</span>
              </span>
            </label>

            <!-- ✅ Acciones custom: lápiz AZUL + basura ROJA -->
            <div v-if="campo.is_custom && !campo.heredado" class="custom-actions">
              <button
                type="button"
                class="btn-mini primary"
                title="Editar"
                @click.stop="openEditModal(campo)"
              >
                ✎
              </button>
              <button
                type="button"
                class="btn-mini danger"
                title="Eliminar"
                @click.stop="removeCampo(campo)"
              >
                🗑
              </button>
            </div>
          </div>

          <div class="checkboxes">
            <input
              type="checkbox"
              :id="'required-' + campo.name_campo"
              :checked="!!campo.requerido"
              :disabled="!campo.activo || !!campo.mandatorio || campo.heredado"
              class="form-checkbox checkbox-required"
              data-label="O"
              @change="$emit('update-campo', { id: campo.id_campo, name: campo.name_campo, patch: { requerido: $event.target.checked } })"
            />
            <input
              type="checkbox"
              :id="'mandatorio-' + campo.name_campo"
              :checked="!!campo.mandatorio"
              :disabled="!campo.activo || campo.heredado"
              v-show="!evento.permitir_duplicados"
              class="form-checkbox checkbox-mandatorio"
              data-label="M"
              @change="onMandatorioChange(campo, $event)"
            />
          </div>
        </div>

        <!-- BOTON + al final -->
        <button type="button" class="add-card" @click="openAddModal">
          <span class="plus">+</span>
          <span>Agregar campo</span>
        </button>
      </div>
    </transition>

    <div class="rol">
      <input
        type="radio"
        id="asistente"
        name="tipoAsistente"
        value="asistente"
        :checked="evento.tipo_asistente === 'asistente'"
        @change="$emit('set-tipo-asistente', 'asistente')"
      />
      <label for="asistente" class="btn-radio">Asistente</label>

      <input
        type="radio"
        id="asistentevirtual"
        name="tipoAsistente"
        value="asistentevirtual"
        :checked="evento.tipo_asistente === 'asistentevirtual'"
        @change="$emit('set-tipo-asistente', 'asistentevirtual')"
      />
      <label for="asistentevirtual" class="btn-radio">Asistente Virtual</label>
    </div>

    <!-- Modal Add/Edit -->
    <transition name="fade">
      <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
        <div class="modal-card">
          <h3 class="modal-title">{{ modalMode === 'add' ? 'Agregar campo' : 'Editar campo' }}</h3>

          <div class="modal-row">
            <label class="modal-label">Nombre visible en español</label>
            <input
              class="modal-input"
              type="text"
              maxlength="150"
              v-model="draft.nombre_del_campo"
              placeholder="Ej: Cargo / Sector / Mesa..."
            />
          </div>

          <div class="modal-row">
            <label class="modal-label">Nombre visible en inglés (opcional)</label>
            <input
              class="modal-input"
              type="text"
              maxlength="150"
              v-model="draft.nombre_del_campo_en"
              placeholder="Ej: Position / Industry / Table..."
            />
            <small class="modal-hint">
              Se usará en formularios y archivos Excel cuando la interfaz esté en inglés. Si queda vacío, se mostrará el nombre en español.
            </small>
          </div>

          <div class="modal-row">
            <label class="modal-label">Label (para validaciones)</label>
            <input
              class="modal-input"
              type="text"
              v-model="draft.name_campo"
              placeholder="Ej: cargo (solo letras/números/_)"
              @blur="draft.name_campo = slugify(draft.name_campo)"
            />
            <small class="modal-hint"
              >Se guarda como clave interna del campo (ej: <b>cargo</b>, <b>mesa_numero</b>)</small
            >
          </div>

          <!-- tipo UI -->
          <div class="modal-row">
            <label class="modal-label">Control</label>
            <select class="modal-input" v-model="draft.tipo_ui" @change="onTipoUiChange">
              <option value="input">Input normal</option>
              <option value="select">Select (una opción)</option>
              <option value="multicheck">Multi-check (varias)</option>
            </select>
            <small class="modal-hint">
              Select/Multi usan opciones configurables. El valor luego se guardará en meta como string (multi: <b>op1,op2</b>).
            </small>
          </div>

          <!-- Tipo de dato / sanitizado -->
          <div v-if="draft.tipo_ui === 'input'" class="modal-row">
            <label class="modal-label">Tipo de dato (sanitizado)</label>
            <select class="modal-input" v-model="draft.tipo_campo">
              <option value="texto">texto</option>
              <option value="numero">numero</option>
              <option value="email">email</option>
              <option value="sanitizado">sanitizado</option>
            </select>
            <small class="modal-hint">
              Solo aplica para inputs. En Select/Multi se fuerza a <b>texto</b>.
            </small>
          </div>

          <!-- opciones select/multi -->
          <div v-if="draft.tipo_ui === 'select' || draft.tipo_ui === 'multicheck'" class="modal-row">
            <label class="modal-label">Opciones (una por línea)</label>
            <textarea
              class="modal-input modal-textarea"
              v-model="draft.opciones_raw"
              rows="6"
              placeholder="Ej:
Ventas
Marketing
Operaciones"
            />
            <small class="modal-hint">
              Se guardará como array en <b>campos_obligatorios</b>. Luego, en meta: select = valor, multi = valores separados por coma.
            </small>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-modal" @click="closeModal">Cancelar</button>
            <button type="button" class="btn-modal primary" @click="saveModal">
              {{ modalMode === 'add' ? 'Agregar' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="js">
import { defineProps, defineEmits, ref } from 'vue'

const props = defineProps({
  evento: { type: Object, required: true },
  campos: { type: Array, required: true },
  camposVisible: { type: Boolean, required: true }
})

const emit = defineEmits([
  'toggle-duplicados',
  'update-campo',
  'set-tipo-asistente',
  'add-campo',
  'edit-campo',
  'remove-campo',
  'reorder-campos'
])

const RESERVED_CUSTOM_LABELS = new Set([
  'staff_id',
  'group_id',
  'evento_id',
  'escarapela',
  'nombres',
  'apellidos',
  'numero_de_documento',
  'correo_electronico',
  'tipo_de_documento',
  'telefono',
  'ciudad',
  'pais',
  'departamento',
  'categoria',
  'empresa',
  'profesion',
  'especialidad',
  'direccion',
  'indicativo_de_telefono',
  'estado',
  'autorizado',
  'creado',
  'editado',
  'borrado',
  'extras',
  'is_modified',
  'is_saved',
  'ismodified',
  'issaved'
])

function onMandatorioChange(campo, e) {
  const checked = e.target.checked
  const patch = checked ? { mandatorio: true, requerido: true } : { mandatorio: false }
  emit('update-campo', { id: campo.id_campo, name: campo.name_campo, patch })
}

function onActivoChange(campo, e) {
  const checked = e.target.checked
  if (!checked) {
    emit('update-campo', {
      id: campo.id_campo,
      name: campo.name_campo,
      patch: { activo: false, requerido: false, mandatorio: false }
    })
  } else {
    emit('update-campo', { id: campo.id_campo, name: campo.name_campo, patch: { activo: true } })
  }
}


/* ===== Drag & Drop orden ===== */
const draggingIndex = ref(null)
const dragOverIndex = ref(null)

function onDragStart(idx, event) {
  if (props.campos?.[idx]?.heredado) {
    event?.preventDefault?.()
    return
  }
  draggingIndex.value = idx
  dragOverIndex.value = idx
  if (event?.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.setData('text/plain', String(idx))
  }
}

function onDragEnter(idx) {
  dragOverIndex.value = idx
}

function onDrop(idx) {
  const fromIndex = Number(draggingIndex.value)
  const toIndex = Number(idx)
  if (props.campos?.[toIndex]?.heredado) {
    onDragEnd()
    return
  }
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) {
    onDragEnd()
    return
  }
  if (fromIndex !== toIndex) {
    emit('reorder-campos', { fromIndex, toIndex })
  }
  onDragEnd()
}

function onDragEnd() {
  draggingIndex.value = null
  dragOverIndex.value = null
}

/* ===== Modal Add/Edit ===== */
const showModal = ref(false)
const modalMode = ref('add') // 'add' | 'edit'
const editingPrevName = ref('')

const draft = ref({
  nombre_del_campo: '',
  nombre_del_campo_en: '',
  name_campo: '',
  tipo_campo: 'texto',
  tipo_ui: 'input',
  opciones_raw: ''
})

function slugify(val) {
  const s = String(val || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  return s
}

function parseOpciones(raw) {
  const lines = String(raw || '')
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)

  const seen = new Set()
  const out = []
  for (const l of lines) {
    if (!seen.has(l)) {
      seen.add(l)
      out.push(l)
    }
  }
  return out
}

function opcionesToRaw(arr) {
  if (!Array.isArray(arr)) return ''
  return arr
    .filter(Boolean)
    .map(s => String(s).trim())
    .filter(Boolean)
    .join('\n')
}

function openAddModal() {
  modalMode.value = 'add'
  editingPrevName.value = ''
  draft.value = {
    nombre_del_campo: '',
    nombre_del_campo_en: '',
    name_campo: '',
    tipo_campo: 'texto',
    tipo_ui: 'input',
    opciones_raw: ''
  }
  showModal.value = true
}

function openEditModal(campo) {
  modalMode.value = 'edit'
  editingPrevName.value = campo.name_campo

  draft.value = {
    nombre_del_campo: campo.nombre_del_campo || '',
    nombre_del_campo_en: campo.nombre_del_campo_en || '',
    name_campo: campo.name_campo || '',
    tipo_campo: campo.tipo_campo || 'texto',
    tipo_ui: campo.tipo_ui || 'input',
    opciones_raw: opcionesToRaw(campo.opciones || [])
  }

  if (draft.value.tipo_ui !== 'input') {
    draft.value.tipo_campo = 'texto'
  }

  showModal.value = true
}

function closeModal() {
  showModal.value = false
}

function onTipoUiChange() {
  if (draft.value.tipo_ui === 'input') return
  draft.value.tipo_campo = 'texto'
}

function saveModal() {
  const nombre = String(draft.value.nombre_del_campo || '').trim()
  const nombreEn = String(draft.value.nombre_del_campo_en || '').trim()
  const name = slugify(draft.value.name_campo)
  const tipoUi = draft.value.tipo_ui || 'input'
  const tipo = tipoUi === 'input' ? (draft.value.tipo_campo || 'texto') : 'texto'

  if (!nombre) return window.alert('Escribe el nombre visible del campo.')
  if (!name) return window.alert('Escribe el label (clave) del campo.')
  if (RESERVED_CUSTOM_LABELS.has(name)) {
    return window.alert(`El label "${name}" está reservado por el sistema. Usa una clave distinta, por ejemplo "tipo_${name}".`)
  }

  const exists = props.campos.some(c => c?.name_campo === name)

  let opciones = []
  if (tipoUi === 'select' || tipoUi === 'multicheck') {
    opciones = parseOpciones(draft.value.opciones_raw)
    if (opciones.length < 1) return window.alert('Agrega al menos 1 opción.')
  }

  if (modalMode.value === 'add') {
    if (exists) return window.alert('Ya existe un campo con ese label.')

    emit('add-campo', {
      id_campo: `custom_${name}`,
      nombre_del_campo: nombre,
      nombre_del_campo_en: nombreEn,
      name_campo: name,
      tipo_campo: tipo,
      tipo_ui: tipoUi,
      opciones,
      activo: true,
      requerido: false,
      mandatorio: false,
      is_custom: true
    })
    closeModal()
    return
  }

  const prev = editingPrevName.value
  if (!prev) return window.alert('No se pudo identificar el campo a editar.')
  if (name !== prev && exists) return window.alert('Ya existe otro campo con ese label.')

  emit('edit-campo', {
    prev_name: prev,
    patch: {
      id_campo: `custom_${name}`,
      nombre_del_campo: nombre,
      nombre_del_campo_en: nombreEn,
      name_campo: name,
      tipo_campo: tipo,
      tipo_ui: tipoUi,
      opciones,
      is_custom: true
    }
  })

  closeModal()
}

function removeCampo(campo) {
  const ok = window.confirm(`¿Eliminar el campo "${campo.nombre_del_campo || campo.name_campo}"?`)
  if (!ok) return
  emit('remove-campo', { id: campo.id_campo, name: campo.name_campo })
}
</script>

<style scoped lang="scss">
.switch-container {
  display: flex;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
}
.switch-container.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.family-notice {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #c4b5fd;
  border-radius: 10px;
  background: #f5f3ff;
  color: #4c1d95;
}
.rol {
  display: flex;
  justify-content: space-between;
}
.switch-bg {
  width: 4rem;
  height: 2rem;
  border-radius: 9999px;
  transition: background-color 0.2s;
}
.switch-dot {
  width: 1.5rem;
  height: 1.5rem;
  background-color: white;
  border-radius: 50%;
  position: absolute;
  top: 0.25rem;
  transition: transform 0.2s;
}

#tabla_campos {
  padding-bottom: 30px;
}

.campo-registro {
  gap: 10px;
}

.drag-handle {
  width: 28px;
  min-width: 28px;
  height: 42px;
  border-radius: 10px;
  border: 1px dashed rgba(0, 0, 0, 0.18);
  background: rgba(0, 0, 0, 0.04);
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3px;
  align-content: center;
  justify-items: center;
  padding: 8px 6px;
  cursor: grab;
  user-select: none;
}
.drag-handle span {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.45);
}
.campo-registro.is-dragging {
  opacity: 0.6;
}
.campo-registro.is-drag-over {
  outline: 2px dashed rgba(37, 99, 235, 0.45);
  outline-offset: 4px;
  border-radius: 10px;
}

.campo-left {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

/* Label */
.campo-registro label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: gray;
  color: white;
  border-radius: 5px;
  transition: background-color 0.3s;
  cursor: pointer;
  flex: 1 1 auto;
  min-width: 0;

  &::selection {
    background: transparent;
  }
  &:hover {
    background: darken(gray, 10%);
  }
}

.label-text {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.tag-custom {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.translation-preview {
  max-width: 260px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.82);
  font-size: 11px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-inherited {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #ddd6fe;
  border: 1px solid #c4b5fd;
  color: #4c1d95;
}

.tag-ui {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
}

/* ✅ Acciones (edit + delete) */
.custom-actions {
  display: flex;
  gap: 6px;
  margin-left: auto;
  position: relative;
  z-index: 5; /* para que el label NO tape los botones */
}

.btn-mini {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.2s ease;
}
.btn-mini:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.18);
}

/* ✅ Azul para editar */
.btn-mini.primary {
  background: rgba(37, 99, 235, 0.35);
  border-color: rgba(37, 99, 235, 0.45);
}
.btn-mini.primary:hover {
  background: rgba(37, 99, 235, 0.5);
}

/* Rojo para eliminar */
.btn-mini.danger {
  background: rgba(255, 0, 0, 0.18);
  border-color: rgba(255, 0, 0, 0.25);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.checkboxes {
  display: flex;
  align-items: center;
}

.checkbox-required,
.checkbox-mandatorio {
  position: relative;
  width: 20px;
  height: 20px;
  cursor: pointer;
  transform: scale(1.5);
  margin-left: 15px;
  -webkit-appearance: none;
  background-color: #eee;
  border-radius: 4px;
  vertical-align: middle;

  &:before {
    content: attr(data-label);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: black;
  }

  &:checked {
    &:before {
      font-weight: bold;
      color: #fff;
    }
    background-color: green;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.checkbox-mandatorio:checked {
  background-color: orange;
  &:before {
    color: #000;
  }
}

.btn-radio {
  display: block;
  width: 49%;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  border: none;
  color: white;
  background-color: gray;
  transition: background-color 0.3s ease;
  border-radius: 5px;
}
input[type='radio'] {
  display: none;
}
input[type='radio']:checked + label {
  background-color: #008000;
}
input[type='radio']:not(:checked) + label {
  background-color: #808080;
}

.campo-registro label.activo {
  background: green;
}

#espacio_campos {
  width: 80vw;
  max-width: 80%;
  margin: 0 auto;
}

/* Botón + */
.add-card {
  grid-column: 1 / -1;
  width: 100%;
  padding: 14px;
  border-radius: 10px;
  border: 2px dashed rgba(0, 0, 0, 0.2);
  background: rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 700;
  transition: transform 0.12s ease, background 0.2s ease;
}
.add-card:hover {
  transform: translateY(-1px);
  background: rgba(0, 0, 0, 0.06);
}
.plus {
  font-size: 20px;
  line-height: 1;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 16px;
}

.modal-card {
  width: 100%;
  max-width: 520px;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.modal-title {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 12px;
}

.modal-row {
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-label {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}

.modal-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  outline: none;
}
.modal-input:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.35);
}

.modal-textarea {
  resize: vertical;
  min-height: 120px;
}

.modal-hint {
  color: #6b7280;
  font-size: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn-modal {
  padding: 10px 12px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  font-weight: 700;
  cursor: pointer;
}
.btn-modal.primary {
  background: #2563eb;
  color: #fff;
  border-color: #1d4ed8;
}
.btn-modal.primary:hover {
  background: #1d4ed8;
}
</style>
